import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import type { GalleryAllImage, PhotoReviewStatus } from '../types';

export interface ImageApprovalHook {
  pendingImages: GalleryAllImage[];
  isLoading: boolean;
  error: string | null;
  fetchPendingImages: () => Promise<GalleryAllImage[]>;
  approveImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string) => Promise<boolean>;
  rejectImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string) => Promise<boolean>;
  flagImage: (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string) => Promise<boolean>;
  updateImageStatus: (imageId: number, newStatus: PhotoReviewStatus, imageType?: 'gallery_photo' | 'job_image', reason?: string) => Promise<boolean>;
}

export const useImageApprovals = (): ImageApprovalHook => {
  const [pendingImages, setPendingImages] = useState<GalleryAllImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchPendingImages = useCallback(async (): Promise<GalleryAllImage[]> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔍 Fetching pending images...');

      // Try to query the unified view first
      const { data: allImagesData, error: allImagesError } = await supabase
        .from('gallery_all_images')
        .select('*')
        .eq('review_status', 'pending')
        .eq('image_type', 'job_image')
        .order('created_at', { ascending: false })
        .limit(100);

      if (allImagesError) {
        console.warn('❌ gallery_all_images view not available:', allImagesError.message);

        // Fallback to franchise_image_jobs table
        const { data: jobImagesData, error: jobImagesError } = await supabase
          .from('franchise_image_jobs')
          .select('*')
          .eq('review_status', 'pending')
          .order('created_on', { ascending: false })
          .limit(100);

        if (jobImagesError) {
          console.warn('❌ franchise_image_jobs table not available:', jobImagesError.message);
          setPendingImages([]);
          setIsLoading(false);
          return [];
        }

        console.log('✅ Found images in franchise_image_jobs table:', jobImagesData?.length || 0);

        // Transform to GalleryAllImage format
        const transformedImages: GalleryAllImage[] = (jobImagesData || []).map(image => ({
          image_type: 'job_image' as const,
          original_id: image.id,
          gallery_id: 0,
          image_url: image.image_url,
          caption: image.file_name,
          review_status: image.review_status as PhotoReviewStatus,
          created_at: image.created_on || new Date().toISOString(),
          job_id: image.job_id,
          job_number: image.job_number,
          franchise_region: image.franchise_region,
        }));

        setPendingImages(transformedImages);
        setIsLoading(false);
        return transformedImages;
      }

      console.log('✅ Found images in gallery_all_images view:', allImagesData?.length || 0);

      setPendingImages(allImagesData || []);
      setIsLoading(false);
      return allImagesData || [];
    } catch (error) {
      console.error('Error fetching pending images:', error);
      setError(null);
      setPendingImages([]);
      setIsLoading(false);
      return [];
    }
  }, []);

  const updateImageStatus = useCallback(async (
    imageId: number,
    newStatus: PhotoReviewStatus,
    imageType: 'gallery_photo' | 'job_image' = 'job_image',
    reason?: string
  ): Promise<boolean> => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to perform approval actions',
        variant: 'destructive'
      });
      return false;
    }

    try {
      // Try to use Edge Function first
      const { data: edgeFunctionResult, error: edgeFunctionError } = await supabase.functions.invoke(
        'update-image-status',
        {
          body: {
            image_id: imageId,
            new_status: newStatus,
            image_type: imageType,
            user_id: user.id,
            reason: reason
          }
        }
      );

      if (!edgeFunctionError && edgeFunctionResult?.success) {
        toast({
          title: 'Success',
          description: `Image ${newStatus} successfully`,
          variant: 'default'
        });

        setPendingImages(prev => prev.filter(image => image.original_id !== imageId));
        return true;
      }

      console.warn('Edge function failed or unavailable, falling back to direct update:', edgeFunctionError);

      // Fallback to direct database update
      const tableName = imageType === 'job_image' ? 'franchise_image_jobs' : 'gallery_photos';

      const updateData: { review_status: PhotoReviewStatus; processed_on?: string } = {
        review_status: newStatus
      };

      if (imageType === 'job_image' && (newStatus === 'approved' || newStatus === 'under_review')) {
        updateData.processed_on = new Date().toISOString();
      }

      const { error: directError } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', imageId);

      if (directError) {
        console.error('Direct update error:', directError);
        toast({
          title: 'Error',
          description: `Failed to ${newStatus} image: ${directError.message}`,
          variant: 'destructive'
        });
        return false;
      }

      toast({
        title: 'Success',
        description: `Image ${newStatus} successfully`,
        variant: 'default'
      });

      setPendingImages(prev => prev.filter(image => image.original_id !== imageId));
      return true;
    } catch (error) {
      console.error(`Error updating image status to ${newStatus}:`, error);
      toast({
        title: 'Error',
        description: `Failed to ${newStatus} image: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      });
      return false;
    }
  }, [toast, user]);

  const approveImage = useCallback(async (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string): Promise<boolean> => {
    return updateImageStatus(imageId, 'approved', imageType, reason);
  }, [updateImageStatus]);

  const rejectImage = useCallback(async (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string): Promise<boolean> => {
    return updateImageStatus(imageId, 'rejected', imageType, reason);
  }, [updateImageStatus]);

      const flagImage = useCallback(async (imageId: number, imageType?: 'gallery_photo' | 'job_image', reason?: string): Promise<boolean> => {
        return updateImageStatus(imageId, 'under_review', imageType, reason);
      }, [updateImageStatus]);

  return {
    pendingImages,
    isLoading,
    error,
    fetchPendingImages,
    approveImage,
    rejectImage,
    flagImage,
    updateImageStatus
  };
};
